import React, { useEffect, useState, useRef } from 'react';
import { Image } from 'antd';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { AsyncThunkAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { ActionResponse } from '../../models/actionResponse/types';
import { BrandCompany, ExternalProduct, FavoriteProduct, Product } from '../../models/product/types';
import { Division, Section } from '../../models/productDivision/types';
import { InvisibleButton, Text, SpaceEnd } from '../../styles/common.styles';
import { ProductsTabHeader } from '../../pages/projectInfo/tabs/productsTab/ProductsTab.styles';
import { blueLink } from '../../styles/colors';
import FavoriteDivisionItem from './favoriteDivisionItem/FavoriteDivisionItem';
import {
    FavoritePageContainer,
    FavoritePageContent,
    FavoriteSpin,
    FavoriteTabContent,
    NoProductsTitleContainer,
    NoProductsTitleContainerFiltered,
    OpenCloseAllButton,
    FoundResultsCount,
    ProductFilterContainer,
} from './FavoriteProducts.styles';
import { MergedFavoriteProduct } from './FavoriteProducts.types';
import {
    GetProductIdsByPackageIdResponse,
    ProductReferences,
} from '../../redux/actions/productActions/types';
import {
    getProductIdsByPackageIdAction,
    getProductReferencesAction,
} from '../../redux/actions/productActions';
import { FavoriteType } from '../../models/product/enums';
import { GetProductsFromTransparencyCatalogAPIResponse, ProductsFilterData } from '../../redux/actions/transparencyCatalogActions/types';
import { addFavoriteInBulkAction } from '../../redux/actions/favoriteProductsActions';
import { AddFavoriteInBulkResponse } from '../../redux/actions/favoriteProductsActions/types';
import ExistingProductsModal from '../existingProductsModal/ExistingProductsModal';
import {
    getCompaniesOfProducts,
    getExternalProductsAction,
} from '../../redux/actions/transparencyCatalogActions';
import { getFavoriteProductTagsByFavoriteProductIdsAction } from '../../redux/actions/favoriteProductTagActions';
import { FavoriteProductTag } from '../../redux/reducers/FavoriteProductTagReducer/FavoriteProductTagSlice';
import FavoriteProductsAction from '../common/favoriteProductsAction/FavoriteProductsAction';

import ProductsSearch from '../products/productsTable/ProductsSearch';
import ProductsFilterButton from '../products/productsFilterModal/ProductsFilterButton';
import ProductsFilterModal, {
    ProductFilters,
} from '../products/productsFilterModal/ProductsFilterModal';
import {
    matchesMasterFormatSearch,
    MASTERFORMAT_SEARCH_PLACEHOLDER,
} from '../../helpers/masterFormatSearch';
import { getUserByIdAction } from '../../redux/actions/userActions';
import { UserByIdResponse } from '../../redux/actions/userActions/types';
import { setStoredUser } from '../../redux/reducers/authReducer/AuthSlice';
import { FavoriteProductTagsProvider } from '../../pages/projectInfo/tabs/productsTab/favoriteProductTagsContext/FavoriteProductTagsContext';
import { MaterialsFiltersIds } from '../../types/materialsFilters.enum';
import { TAGS_BATCH_SIZE } from './FavoriteProducts.consts';
import { normalizeRating, RATING_SYSTEM_TITLE_MAP } from '../../constants/ratingSystems';

interface Params {
    projectId: string;
}

interface Props {
    favoriteType: FavoriteType;
    removedProducts: Array<MergedFavoriteProduct>;
    selectedProducts: Array<MergedFavoriteProduct>;
    productId?: string;
    setSelectedProducts: (selectedProducts: Array<MergedFavoriteProduct>) => void;
    handleSelectProduct: (product: MergedFavoriteProduct, checked: boolean) => void;
    setFavoriteProducts: React.Dispatch<React.SetStateAction<ExternalProduct[]>>;
    removeFavorites: () => void;
    addProductsToProject: () => void;
    addProductsToFavorite: () => void;
    action: () => AsyncThunkAction<
        ActionResponse<Array<FavoriteProduct>> | undefined,
        undefined,
        {}
    >;
    isFilterMode?: boolean;
    isFilteringInProgress?: boolean;
    filteredDivisions: Array<Division<MergedFavoriteProduct>>;
    setFilteredDivisions: (
        divisions: Array<Division<MergedFavoriteProduct>>,
    ) => Array<Division<MergedFavoriteProduct>>;
    setFilteredMode: (filterMode: boolean) => boolean;
    setFilteringInProgress: (filterInProgress: boolean) => boolean;
}

const FavoriteProducts: React.FC<Props> = ({
    removedProducts,
    favoriteType,
    selectedProducts,
    productId,
    setSelectedProducts,
    handleSelectProduct,
    setFavoriteProducts,
    removeFavorites,
    addProductsToFavorite,
    addProductsToProject,
    action,
    isFilterMode,
    isFilteringInProgress,
    filteredDivisions,
    setFilteredDivisions,
    setFilteredMode,
    setFilteringInProgress,
}) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const history = useHistory();
    const params = useParams<Params>();
    const queryParams = new URLSearchParams(location.search);
    const selector = useAppSelector((combinedState) => combinedState);
    const { authReducer } = selector;

    const existingProductIds = useRef(Array<number>());
    const [existingProducts, setExistingProducts] = useState({
        products: Array<{ id: number; name: string }>(),
        productIds: Array<number>(),
        isModalOpen: false,
    });

    const [state, setState] = useState({
        divisions: Array<Division<MergedFavoriteProduct>>(),
        filteredDivisions: Array<Division<MergedFavoriteProduct>>(),
        pending: true,
        searchString: null,
    });

    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState<ProductFilters>({
        ratingSystems: [],
    });

    const [references, setReferences] = useState<ProductReferences | null>(null);

    const packageId = queryParams.get('packageId');
    useEffect(() => {
        window.scroll(0, 0);
        if (!state.searchString) {
            getData();
        }
        fetchUserData();
    }, []);

    const getData = async (filters?: ProductFilters, searchString?: string) => {
        await getProductIdsByPackageId();
        await getFavorites(filters, searchString);
        await getProductReferences();
    };

    const fetchUserData = async () => {
        if (authReducer.user?.id) {
            const { payload } = await dispatch(
                getUserByIdAction(authReducer.user?.id.toString())
            ) as ActionResponse<Array<UserByIdResponse>>;

            if (payload.data && payload.data.length > 0) {
                dispatch(setStoredUser(payload.data[0]));
            }
        }
    }

    useEffect(() => {
        if (removedProducts && removedProducts.length) {
            removeProducts();
        }
    }, [removedProducts]);

    const changeSearch = (searchString: string) => {
        setState((state) => ({
            ...state,
            searchString: searchString,
        }));
    };

    const submitSearch = (e) => {
        setFilteringInProgress(true);
        setFilteredMode(true);
        if (!state.searchString) {
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }
        getData(filters, e.target.value);
    };

    const cleanFilter = () => {
        const value = '';
        setFilteringInProgress(true);
        changeSearch(value);
        if (!filters.ratingSystems.length) {
            setFilteredMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
        } else {
            setFilteringInProgress(true);
            setFilteredMode(true);
            getData(filters, value);
        }
    };

    const getProductReferences = async () => {
        const { payload } = (await dispatch(
            getProductReferencesAction(),
        )) as ActionResponse<ProductReferences>;
        setReferences(payload.data);
    };
    const getProductIdsByPackageId = async () => {
        if (packageId) {
            const { payload } = (await dispatch(
                getProductIdsByPackageIdAction({
                    productPackageID: packageId,
                }),
            )) as GetProductIdsByPackageIdResponse;
            if (payload.result && payload.result.length) {
                await addFavoriteInBulk(payload.result.map((p) => p.productSelectedOnTcId));
            }
        }
    };

    const addFavoriteInBulk = async (productFavoriteIds: Array<number>) => {
        const { payload } = (await dispatch(
            addFavoriteInBulkAction({ productFavoriteIds, favoriteType }),
        )) as ActionResponse<AddFavoriteInBulkResponse>;
        if (payload.data && payload.data.existedProductIds.length) {
            existingProductIds.current = payload.data.existedProductIds;
        }
    };

    const getFavorites = async (filters?: ProductFilters, searchString?: string) => {
        const { payload } = (await dispatch(action())) as ActionResponse<Array<FavoriteProduct>>;
        if (payload.data) {
            getProductsFromTransparencyCatalog(payload.data, filters, searchString);
        }
    };

    const fetchTagsInBatches = async (ids: number[]): Promise<FavoriteProductTag[]> => {
        const allTags: FavoriteProductTag[] = [];
        for (let i = 0; i < ids.length; i += TAGS_BATCH_SIZE) {
            const batch = ids.slice(i, i + TAGS_BATCH_SIZE);
            const result = await dispatch(
                getFavoriteProductTagsByFavoriteProductIdsAction(batch),
            ) as { payload: any };
            const batchTags: FavoriteProductTag[] = result.payload ?? [];
            allTags.push(...batchTags);
        }
        return allTags;
    };

    const getCompanyData = async (externalProducts: ExternalProduct[]) => {
        const companies = await getCompaniesOfProducts(externalProducts, dispatch);
        return externalProducts.map((ep) => ({
            ...ep,
            companyLevel: companies.find((c) => c.id === ep.companyID)?.level,
            listingUrl: companies.find((c) => c.id === ep.companyID)?.listingUrl,
        }));
    };

    const getProductsFromTransparencyCatalog = async (
        favoriteProducts: Array<FavoriteProduct>,
        filters?: ProductFilters,
        searchString?: string,
    ) => {
        const productIds = favoriteProducts.map((favorite) => favorite.product.productId);
        if (!productIds || !productIds.length) {
            setState((state) => ({ ...state, pending: false }));
            return;
        }
        const { payload } = (await dispatch(
            getExternalProductsAction({ productIds }),
        )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;

        if (payload.data) {
            let externalProducts = Object.values(payload.data.mfSection).reduce(
                (agg, item: any) => agg.concat(item),
                Array<ExternalProduct>(),
            );
            externalProducts = await getCompanyData(externalProducts);
            setFavoriteProducts(externalProducts);
            const products = mergeProducts(externalProducts, favoriteProducts);

            const allFavoriteProductIds = products
                .map((p) => p.id)
                .filter((id): id is number => id != null);

            const allTags = allFavoriteProductIds.length > 0
                ? await fetchTagsInBatches(allFavoriteProductIds)
                : [];

            let freshTagsMap: Record<number, FavoriteProductTag[]> | undefined;

            if (filters?.tags?.length) {
                freshTagsMap = {};
                for (const tag of allTags) {
                    const pid = tag.favoriteProductId;
                    if (!freshTagsMap[pid]) {
                        freshTagsMap[pid] = [];
                    }
                    freshTagsMap[pid].push(tag);
                }
            }
            const divisions = new Map();
            products.forEach((product) => {
                const key = parseInt(product.mfDivision.number[0] + product.mfDivision.number[1]);
                const highestSection = product.mfSectionLevel1 || product.mfSection;
                if (divisions.has(key)) {
                    if (divisions.get(key).sections.has(highestSection.number.slice(0, 5))) {
                        divisions
                            .get(key)
                            .sections.get(highestSection.number.slice(0, 5))
                            .products.push(product);
                    } else {
                        divisions.get(key).sections.set(highestSection.number.slice(0, 5), {
                            id: highestSection.number.slice(0, 5),
                            key: parseInt(highestSection.number[3] + highestSection.number[4]),
                            name: highestSection.label,
                            clicked: false,
                            products: [product],
                        });
                    }
                } else {
                    divisions.set(key, {
                        id: product.mfDivision.number,
                        name: product.mfDivision.label,
                        key: key,
                        sections: new Map([
                            [
                                highestSection.number.slice(0, 5),
                                {
                                    id: highestSection.number.slice(0, 5),
                                    key: parseInt(
                                        highestSection.number[3] + highestSection.number[4],
                                    ),
                                    name: highestSection.label,
                                    clicked: false,
                                    products: [product],
                                },
                            ],
                        ]),
                        clicked: false,
                    });
                }
            });
            for (const division of divisions.values()) {
                division.sections = [...division.sections.values()].sort(
                    (a: Section<FavoriteProduct>, b: Section<FavoriteProduct>) => a.key - b.key,
                );
            }
            const uniqueDivisions = [...divisions.values()];
            const selectedProduct = externalProducts.find(
                (product) => product.id.toString() === productId,
            );
            if (existingProductIds.current && existingProductIds.current.length) {
                setExistingProducts({
                    ...existingProducts,
                    products: products
                        .filter((cp) =>
                            existingProductIds.current.some((id) => id === cp.product.productId),
                        )
                        .map((product) => ({ name: product.productName, id: product.id })),
                    isModalOpen: true,
                });
            }
            if (selectedProduct) {
                showSelectedProduct(selectedProduct, uniqueDivisions);
            }
            if (packageId) {
                window.history.replaceState(
                    null,
                    '',
                    `${favoriteType === FavoriteType.MyFavorite ? 'my-favorites' : 'favorites'}`,
                );
            }
            setState((state) => ({
                ...state,
                divisions: uniqueDivisions.toSorted((a, b) => a.key - b.key),
                pending: false,
            }));

            const uniqueFilteredDivisions = filterProducts(
                uniqueDivisions,
                filters,
                searchString,
                freshTagsMap,
            );
            setFilteredDivisions([...uniqueFilteredDivisions.toSorted((a, b) => a.key - b.key)]);
            setFilteringInProgress(false);
        }
    };

    const showSelectedProduct = (
        product: ExternalProduct,
        divisions: Array<Division<MergedFavoriteProduct>>,
    ) => {
        const divisionIndex = divisions.findIndex((d) => d.id === product.mfDivision.number);
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        if (divisionIndex !== -1) {
            divisions[divisionIndex].clicked = true;
            const sectionIndex = divisions[divisionIndex].sections.findIndex(
                (s) => s.id === highestSection.number.slice(0, 5),
            );
            if (sectionIndex !== -1) {
                divisions[divisionIndex].sections[sectionIndex].clicked = true;
                setTimeout(() => {
                    document.getElementById(`${product.id}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }, 500);
            }
        }
    };
    const mergeProducts = (
        externalProducts: Array<ExternalProduct>,
        favoriteProducts: Array<FavoriteProduct>,
    ): Array<MergedFavoriteProduct> => {
        return favoriteProducts.reduce((agg, value) => {
            const currentProduct = externalProducts.find(
                (product) => product.id === value.product.productId,
            );
            if (currentProduct) {
                agg.push({
                    ...currentProduct,
                    ...value,
                    selected: false,
                });
            }
            return agg;
        }, Array<MergedFavoriteProduct>());
    };

    const handleDivisionOpen = (id: string) => {
        const index = state.divisions.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentDivisions = state.divisions;
            currentDivisions[index].clicked = !currentDivisions[index].clicked;
            setState((state) => ({ ...state, divisions: currentDivisions }));
        }
    };

    const handleFilteredDivisionOpen = (id: string) => {
        const index = filteredDivisions.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentDivisions = filteredDivisions;
            currentDivisions[index].clicked = !currentDivisions[index].clicked;
            setFilteredDivisions(currentDivisions);
        }
    };

    const handleSectionOpen = (divisionId: string, sectionId: string) => {
        const divisionIndex = state.divisions.findIndex((division) => division.id === divisionId);
        const sectionIndex = state.divisions[divisionIndex].sections.findIndex(
            (section) => section.id === sectionId,
        );
        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = state.divisions;
            currentDivisions[divisionIndex].sections[sectionIndex].clicked =
                !currentDivisions[divisionIndex].sections[sectionIndex].clicked;
            setState((state) => ({ ...state, divisions: currentDivisions }));
        }
    };

    const handleFilteredSectionOpen = (divisionId: string, sectionId: string) => {
        const divisionIndex = filteredDivisions.findIndex((division) => division.id === divisionId);
        const sectionIndex = filteredDivisions[divisionIndex].sections.findIndex(
            (section) => section.id === sectionId,
        );
        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = filteredDivisions;
            currentDivisions[divisionIndex].sections[sectionIndex].clicked =
                !currentDivisions[divisionIndex].sections[sectionIndex].clicked;
            setFilteredDivisions(currentDivisions);
        }
    };

    const selectAllProducts = (divisionId: string, sectionId: string, checked: boolean) => {
        const divisionIndex = !isFilterMode
            ? state.divisions.findIndex((division) => division.id === divisionId)
            : filteredDivisions.findIndex((division) => division.id === divisionId);
        const sectionIndex = !isFilterMode
            ? state.divisions[divisionIndex].sections.findIndex(
                (section) => section.id === sectionId,
            )
            : filteredDivisions[divisionIndex].sections.findIndex(
                (section) => section.id === sectionId,
            );
        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = !isFilterMode ? state.divisions : filteredDivisions;
            currentDivisions[divisionIndex].sections[sectionIndex].products = currentDivisions[
                divisionIndex
            ].sections[sectionIndex].products.map((product) => ({ ...product, selected: checked }));
            let currentSelectedProducts = selectedProducts;
            currentDivisions[divisionIndex].sections[sectionIndex].products.forEach((product) => {
                if (
                    product.selected &&
                    !currentSelectedProducts.find((item) => item.id === product.id)
                ) {
                    currentSelectedProducts.push(product);
                }
                if (!product.selected) {
                    currentSelectedProducts = currentSelectedProducts.filter(
                        (currentProduct) => currentProduct.id !== product.id,
                    );
                }
            });
            setSelectedProducts(currentSelectedProducts);
            if (!isFilterMode) {
                setState((state) => ({ ...state, divisions: currentDivisions }));
            } else {
                setState((state) => ({ ...state, filteredDivisions: currentDivisions }));
            }
        }
    };

    const selectProduct = (product: MergedFavoriteProduct, checked: boolean) => {
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        const divisionIndex = !isFilterMode
            ? state.divisions.findIndex((division) => division.id === product.mfDivision.number)
            : filteredDivisions.findIndex((division) => division.id === product.mfDivision.number);
        if (divisionIndex === -1) {
            return;
        }
        const sectionIndex = !isFilterMode
            ? state.divisions[divisionIndex].sections.findIndex(
                (section) => section.id === highestSection.number.slice(0, 5),
            )
            : filteredDivisions[divisionIndex].sections.findIndex(
                (section) => section.id === highestSection.number.slice(0, 5),
            );
        if (sectionIndex === -1) {
            return;
        }
        const productIndex = !isFilterMode
            ? state.divisions[divisionIndex].sections[sectionIndex].products.findIndex(
                (item) => product.id === item.id,
            )
            : filteredDivisions[divisionIndex].sections[sectionIndex].products.findIndex(
                (item) => product.id === item.id,
            );

        if (productIndex === -1) {
            return;
        }

        const currentDivisions = !isFilterMode ? state.divisions : filteredDivisions;
        currentDivisions[divisionIndex].sections[sectionIndex].products[productIndex].selected =
            checked;
        if (!isFilterMode) {
            setState((state) => ({ ...state, divisions: currentDivisions }));
        } else {
            setState((state) => ({ ...state, filteredDivisions: currentDivisions }));
        }
        handleSelectProduct(product, checked);
    };

    const removeProducts = () => {
        removedProducts.forEach((product) => {
            const highestSection = product.mfSectionLevel1 || product.mfSection;
            let divisionIndex = state.divisions.findIndex(
                (division) => division.id === product.mfDivision.number,
            );
            let sectionIndex = state.divisions[divisionIndex].sections.findIndex(
                (section) => section.id === highestSection.number.slice(0, 5),
            );

            let productIndex = state.divisions[divisionIndex].sections[
                sectionIndex
            ].products.findIndex((item) => item.id === product.id);
            let currentDivisions = state.divisions;
            currentDivisions[divisionIndex].sections[sectionIndex].products.splice(productIndex, 1);
            if (!currentDivisions[divisionIndex].sections[sectionIndex].products.length) {
                currentDivisions[divisionIndex].sections.splice(sectionIndex, 1);
                if (!currentDivisions[divisionIndex].sections.length) {
                    currentDivisions.splice(divisionIndex, 1);
                }
            }
            setState((state) => ({ ...state, divisions: currentDivisions }));

            if (isFilterMode) {
                divisionIndex = filteredDivisions.findIndex(
                    (division) => division.id === product.mfDivision.number,
                );
                sectionIndex = filteredDivisions[divisionIndex].sections.findIndex(
                    (section) => section.id === highestSection.number.slice(0, 5),
                );

                productIndex = filteredDivisions[divisionIndex].sections[
                    sectionIndex
                ].products.findIndex((item) => item.id === product.id);
                currentDivisions = filteredDivisions;
                currentDivisions[divisionIndex].sections[sectionIndex].products.splice(
                    productIndex,
                    1,
                );
                if (!currentDivisions[divisionIndex].sections[sectionIndex].products.length) {
                    currentDivisions[divisionIndex].sections.splice(sectionIndex, 1);
                    if (!currentDivisions[divisionIndex].sections.length) {
                        currentDivisions.splice(divisionIndex, 1);
                    }
                }

                setState((state) => ({ ...state, filteredDivisions: currentDivisions }));
            }
        });
    };

    const closeWarningModal = () => {
        setExistingProducts({ ...existingProducts, isModalOpen: false });
    };

    const openCloseAll = async () => {
        let actionCloseAll;

        if (!isFilterMode) {
            actionCloseAll = checkWhatActionNeeded(state.divisions);
            openCloseAllProcess(state.divisions, actionCloseAll);
        } else if (isFilterMode) {
            actionCloseAll = checkWhatActionNeeded(filteredDivisions);
            openCloseAllProcess(filteredDivisions, actionCloseAll);
        }

        let historyPush = '';
        if (favoriteType === 1) {
            historyPush += `/favorites`;
        }
        if (favoriteType === 0) {
            historyPush += `/my-favorites`;
        }
        if (params?.projectId) {
            historyPush += `/` + params.projectId;
        }

        if (favoriteType === 1 || favoriteType === 0) {
            history.push(historyPush);
        }
    };

    const openCloseAllProcess = async (
        divisions: Array<Division<Product>>,
        isActionNeeded: boolean,
    ) => {
        if (!divisions || !divisions.length) {
            return;
        }

        for (const division of divisions) {
            division.clicked = isActionNeeded;
            if (!division.sections || !division.sections.length) {
                return;
            }
            for (const section of division.sections) {
                section.clicked = isActionNeeded;
            }
        }
    };

    const checkWhatActionNeeded = (divisions: Array<Division<Product>>) => {
        for (const division of divisions) {
            if (!division.clicked) {
                return true;
            }
            for (const section of division.sections) {
                if (!section.clicked) {
                    return true;
                }
            }
        }
        return false;
    };

    // Helper function to check if product matches tags filter
    const doesProductMatchTags = (
        product: MergedFavoriteProduct,
        filtersToCheck: ProductFilters | undefined,
        freshTagsMap?: Record<number, FavoriteProductTag[]>,
    ): boolean => {
        if (!filtersToCheck?.tags?.length) {
            return true;
        }
        const favoriteProductExternalId = product.id;
        const productTags: FavoriteProductTag[] | undefined =
            freshTagsMap?.[favoriteProductExternalId] ??
            (selector as any).productTagReducer?.byProductId?.[favoriteProductExternalId]?.tags;
        if (!productTags) {
            return false;
        }
        return filtersToCheck.tags.some((t) =>
            productTags.some((pt) => pt.tagId === t.id || pt.tag?.id === t.id),
        );
    };


    const handleMaterialsFilters = (materialFilter: ProductsFilterData, product: MergedFavoriteProduct) => {
        switch (materialFilter.id) {
            case MaterialsFiltersIds.HPD:
                return Boolean(product.hpd);
            case MaterialsFiltersIds.HPDPW:
                return Boolean(product.perkinsWillPrecautionaryListFree);
            case MaterialsFiltersIds.HPDLBC:
                return Boolean(product.livingBuildingChallengeRedListFree);
            case MaterialsFiltersIds.HPDC2C:
                return Boolean(product.c2cRestrictSubstancesListFree);
            case MaterialsFiltersIds.Declare:
                return Boolean(product.declare);
            case MaterialsFiltersIds.DeclareLBCRedList:
            case MaterialsFiltersIds.DeclareLBCCopliant:
            case MaterialsFiltersIds.DeclareDeclared:
                return product.declareResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CCertified:
                return Boolean(product.c2cResults);
            case MaterialsFiltersIds.C2CCertifiedPlatinum:
            case MaterialsFiltersIds.C2CCertifiedGold:
            case MaterialsFiltersIds.C2CCertifiedSilver:
            case MaterialsFiltersIds.C2CCertifiedBronze:
                return product.c2cResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CMHC:
                return Boolean(product.c2cMHCResults);
            case MaterialsFiltersIds.C2CMHCPlatinum:
            case MaterialsFiltersIds.C2CMHCGold:
            case MaterialsFiltersIds.C2CMHCSilver:
            case MaterialsFiltersIds.C2CMHCBronze:
                return product.c2cMHCResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.LPC:
                return product.lpc?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.MFRI:
                return Boolean(product.mfrInv);
            default:
                return false;
        }
    }

    const filterProducts = (
        divs: Array<Division<MergedFavoriteProduct>>,
        filters?: ProductFilters,
        searchString?: string,
        freshTagsMap?: Record<number, FavoriteProductTag[]>,
    ) => {
        const filteredDs: Division<MergedFavoriteProduct>[] = [];
        const strSearch = searchString ? searchString.toLowerCase() : searchString;
        for (const div of divs.values()) {
            const filteredSections = [];
            for (const sect of div.sections.values()) {
                const filteredProducts = sect.products.filter((product) => {
                    const hasSearchString = !!strSearch;
                    const hasRatingSystems = !!filters?.ratingSystems?.length;

                    const matchesSearch =
                        !hasSearchString ||
                        product.productName.toLowerCase().includes(strSearch) ||
                        product.company?.toLowerCase().includes(strSearch) ||
                        matchesMasterFormatSearch(product, strSearch);

                    const matchesRatingSystems =
                        !hasRatingSystems ||
                        filters.ratingSystems.some((rs) => {
                            const mappedTitle = RATING_SYSTEM_TITLE_MAP[rs.title] ?? rs.title;
                            return (product as any).ratingSystems?.some((rating) =>
                                normalizeRating(rating).includes(normalizeRating(mappedTitle))
                            );
                        });

                    const matchesTags = doesProductMatchTags(product, filters, freshTagsMap);

                    const matchesEpds =
                        !filters?.epdFilters?.length ||
                        filters.epdFilters.some((epd) => {
                            let result = product.epdType?.toLowerCase().includes(epd.title.replace('-', ' ').toLowerCase())
                            if(epd.title.toLowerCase().includes("optimized")){
                                result ||= product.isOptimizedEpd;
                            }
                            return result;
                        });

                    const matchesSMEpds =
                        !filters?.smEpdsFilters?.length ||
                        filters.smEpdsFilters.some((smEpd) =>{
                                let result = product.epdType?.toLowerCase().includes(smEpd.title.replace('-', ' ').toLowerCase()) &&
                                product.epdProgram?.toLowerCase().includes('sminds')
                                if(smEpd.title.toLowerCase().includes("optimized")){
                                   result ||= product.isOptimizedEpd
                                    && product.epdProgram?.toLowerCase().includes("sminds");
                                }
                                return result;
                            }
                        );

                    const matchesEC3 =
                        !filters?.ec3Filters?.length ||
                        filters.ec3Filters.some((ec3) =>
                            product.ec3EmbodiedCarbon?.toLowerCase().includes(ec3.title.toLowerCase())
                        );

                    const matchesRegulations =
                        !filters?.regulationsFilters?.length ||
                        filters.regulationsFilters.some((r) =>
                            product.regulations?.some((regulation) =>
                                regulation.toLowerCase().includes(r.title.toLowerCase())
                            )
                        );

                    const matchesMaterialsFilters =
                        !filters?.materialsFilters?.length ||
                        filters.materialsFilters.some((materialFilter) =>
                            handleMaterialsFilters(materialFilter, product)
                        );

                    const matchesLowEmmitingFilters =
                        !filters?.lowEmittingFilters?.length ||
                        filters.lowEmittingFilters.some((lem) =>
                            (product as any)[lem.id]
                        );

                    const matchesLemRegulations =
                        !filters?.lemRegulationsFilters?.length ||
                        filters.lemRegulationsFilters.some((r) =>
                            (product as any)[r.id]
                        );

                    return matchesSearch &&
                        matchesRatingSystems &&
                        matchesTags &&
                        matchesMaterialsFilters &&
                        matchesLowEmmitingFilters &&
                        matchesLemRegulations &&
                        matchesEpds &&
                        matchesSMEpds &&
                        matchesEC3 &&
                        matchesRegulations;
                });
                if (filteredProducts && filteredProducts.length > 0) {
                    const filteredSection = { ...sect };
                    filteredSection.clicked = true;
                    filteredSection.products = filteredProducts;
                    filteredSections.push(filteredSection);
                }
            }
            if (filteredSections && filteredSections.length > 0) {
                const filteredD = { ...div };
                filteredD.clicked = true;
                filteredD.sections = filteredSections;
                filteredDs.push(filteredD);
            }
        }
        return filteredDs;
    };

    const handleFilters = (filters: ProductFilters) => {
        setFilteringInProgress(true);
        setFilters(filters);

        const hasFilters = Object.values(filters).some((filter) =>
            Array.isArray(filter) ? filter.length > 0 : !!filter
        );
        const hasSearchString = !!state.searchString;

        if (!hasFilters && !hasSearchString) {
            setFilteredMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }

        setFilteredMode(true);
        getData(filters, state.searchString);
    };

    const getBrandAndProductCount = (divisions: Array<Division<Product | BrandCompany>>) => {
        let productsCount = 0;
        const companiesNames = [];
        for (const division of divisions) {
            for (const section of division.sections) {
                const products = section.products;
                productsCount += products.length;

                for (const product of products as Product[]) {
                    if (product.company) {
                        companiesNames.push(product.company);
                    }
                }
            }
        }

        const uniqueCompanyNames = [...new Set(companiesNames)]

        return {
            products: productsCount,
            companies: uniqueCompanyNames.length
        };
    };

    return (
        <FavoriteProductTagsProvider>
            <FavoritePageContainer>
                <ProductsFilterModal
                    open={filterModalOpen}
                    onCancel={() => setFilterModalOpen(false)}
                    onFilter={handleFilters}
                />
                <FavoritePageContent className={`${filterModalOpen ? "shifted" : ""}`}>
                    <ProductsTabHeader>
                        <ProductFilterContainer>
                            <ProductsSearch
                                searchString={state.searchString}
                                placeholder={MASTERFORMAT_SEARCH_PLACEHOLDER}
                                cleanFilter={cleanFilter}
                                changeSearch={changeSearch}
                                handleSearchSubmit={submitSearch}
                            />
                            <ProductsFilterButton onClick={() => filterModalOpen ? setFilterModalOpen(false) : setFilterModalOpen(true)} />
                        </ProductFilterContainer>
                        <SpaceEnd>
                            <FavoriteProductsAction
                                favoriteType={favoriteType}
                                selectedProducts={selectedProducts}
                                addProductsToFavorite={addProductsToFavorite}
                                addProductsToProject={addProductsToProject}
                                removeFavorites={removeFavorites}
                            />
                        </SpaceEnd>
                    </ProductsTabHeader>

                    {isFilterMode && !isFilteringInProgress && (
                        <FoundResultsCount weight={400} size={14} height={18} color={blueLink}>
                            {filteredDivisions.length
                                ? (() => {
                                    const brandAndProductCount = getBrandAndProductCount(filteredDivisions);
                                    const companySuffix = brandAndProductCount.companies > 1 ? 's' : '';
                                    const productSuffix = brandAndProductCount.products > 1 ? 's' : '';

                                    return `${brandAndProductCount.companies} brand${companySuffix} | ${brandAndProductCount.products} product${productSuffix} found`;
                                })()
                                : 'No results found'
                            }
                        </FoundResultsCount>
                    )}

                    {(!isFilterMode && state.divisions.length) ||
                        (isFilterMode && filteredDivisions.length) ? (
                        <InvisibleButton>
                            <OpenCloseAllButton onClick={openCloseAll} className="closeOpenAll">
                                <Text weight={400} size={12} height={14} color={blueLink}>
                                    Open / close all
                                </Text>
                            </OpenCloseAllButton>
                        </InvisibleButton>
                    ) : (
                        ''
                    )}
                    {state.pending || isFilteringInProgress ? (
                        <FavoriteSpin size="large" spinning={state.pending || isFilteringInProgress}>
                            <FavoriteTabContent></FavoriteTabContent>
                        </FavoriteSpin>
                    ) : (
                        <>
                            {!isFilterMode && (
                                <>
                                    {state.divisions.length > 0 ? (
                                        <FavoriteTabContent>
                                            {state.divisions.map((division) => (
                                                <FavoriteDivisionItem
                                                    key={division.id}
                                                    division={division}
                                                    references={references}
                                                    favoriteType={favoriteType}
                                                    handleClicked={handleDivisionOpen}
                                                    handleSectionOpen={handleSectionOpen}
                                                    selectAllProducts={selectAllProducts}
                                                    selectProduct={selectProduct}
                                                />
                                            ))}
                                        </FavoriteTabContent>
                                    ) : (
                                        <FavoriteTabContent>
                                            <NoProductsTitleContainer>
                                                <h2>There are no products here yet</h2>
                                            </NoProductsTitleContainer>
                                        </FavoriteTabContent>
                                    )}
                                </>
                            )}

                            {isFilterMode && (
                                <>
                                    {filteredDivisions.length > 0 ? (
                                        <FavoriteTabContent>
                                            {filteredDivisions.map((division) => (
                                                <FavoriteDivisionItem
                                                    key={division.id}
                                                    division={division}
                                                    references={references}
                                                    favoriteType={favoriteType}
                                                    handleClicked={handleFilteredDivisionOpen}
                                                    handleSectionOpen={handleFilteredSectionOpen}
                                                    selectAllProducts={selectAllProducts}
                                                    selectProduct={selectProduct}
                                                />
                                            ))}
                                        </FavoriteTabContent>
                                    ) : (
                                        <FavoriteTabContent>
                                            <NoProductsTitleContainerFiltered>
                                                <center>
                                                    <Image
                                                        src="src/assets/images/empty-graphic 1.svg"
                                                        preview={false}
                                                        alt="no results found"
                                                    />
                                                    <h2>
                                                        <center>
                                                            Clear search field to
                                                            <br />
                                                            display products and try
                                                            <br />
                                                            your search again.
                                                        </center>
                                                    </h2>
                                                </center>
                                            </NoProductsTitleContainerFiltered>
                                        </FavoriteTabContent>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {existingProducts.isModalOpen && (
                        <ExistingProductsModal
                            isOpen={existingProducts.isModalOpen}
                            closeModal={closeWarningModal}
                            existingProducts={existingProducts.products}
                            addedTo={`${favoriteType === FavoriteType.MyFavorite
                                ? 'Favorite Products Library'
                                : "office's Favorite Products Library"
                                }`}
                            addedFrom={'Transparency Catalog'}
                            currentPage={`${favoriteType === FavoriteType.MyFavorite ? 'my-favorites' : 'favorites'
                                }`}
                        />
                    )}
                </FavoritePageContent>
            </FavoritePageContainer>
        </FavoriteProductTagsProvider>
    );
};

export default FavoriteProducts;
